import { FC, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useActiveSection } from "../hooks";
import { DocsTocGroup, DocsTocItem, DocsTocProps } from "../interfaces";

/**
 * YOSHI-10464: turns the flat list a page declares into groups, so the index can show the skeleton
 * of the page and open only the part being read. A level 2 entry belongs to the last level 1 entry
 * declared before it; a page that declares no level 2 entry gets one childless group per entry and
 * therefore looks exactly as it did before.
 *
 * @param items Entries of the index, in reading order.
 * @returns The same entries, nested.
 */
const buildGroups = (items: DocsTocItem[]): DocsTocGroup[] => {
  const groups: DocsTocGroup[] = [];

  items.forEach((item) => {
    const currentGroup: DocsTocGroup | undefined = groups[groups.length - 1];

    if (item.level === 2 && currentGroup !== undefined) {
      currentGroup.children.push(item);
    } else {
      groups.push({
        id: item.id,
        translationKey: item.translationKey,
        children: [],
      });
    }
  });

  return groups;
};

export const DocsToc: FC<DocsTocProps> = (props) => {
  const { items } = props;
  const { t } = useTranslation();
  const sectionIds = useMemo(() => {
    return items.map((item) => item.id);
  }, [items]);
  const groups = useMemo(() => {
    return buildGroups(items);
  }, [items]);
  // Every section is watched, including the ones of the groups that are closed: the index has to
  // know where the reader is before it can decide which group to open.
  const activeId: string = useActiveSection(sectionIds);

  // YOSHI-10464: the open group is derived from the section being read and from nothing else. The
  // reader never has to open anything to follow along, and clicking a group entry jumps to it,
  // which makes it the active one and opens it: every entry stays reachable.
  const openGroupId: string = useMemo(() => {
    const owner: DocsTocGroup | undefined = groups.find((group) => {
      return (
        group.id === activeId ||
        group.children.some((child) => child.id === activeId)
      );
    });

    return owner !== undefined ? owner.id : "";
  }, [groups, activeId]);

  return (
    // YOSHI-10464: on a wide screen the index is a rail that follows the reading; below lg the page
    // stacks it above the content (the column is reversed).
    <nav
      aria-label={t("docs.common.onThisPage")}
      className="lg:sticky lg:top-32 lg:w-56 lg:shrink-0 lg:self-start"
    >
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">
        {t("docs.common.onThisPage")}
      </p>
      {/* The rail is drawn by each entry and no longer by the list, so the segment that marks the
          active section covers the entry whole. An operation title such as "List the concessions of
          a venue" takes more than one line on a 14rem rail, and cutting it with an ellipsis hid
          exactly the word that said where the link went: entries wrap, they are never trimmed.
          The endpoint reference declares 23 entries, which do not fit on a screen if they are all
          drawn at once, so the operations of a group are drawn only while that group is being read:
          the index stays whole and needs no scrollbar of its own.
          Each entry is a router link and not a bare anchor: the site mounts `ScrollRestoration`,
          which puts the browser in manual scroll restoration, and with it a plain `href="#id"`
          changes the address bar without moving the page at all. Going through the router makes the
          jump happen, and it lands where `scroll-padding-top` and `scroll-mt-6` say. */}
      <ul className="text-sm leading-snug">
        {groups.map((group) => {
          const hasChildren: boolean = group.children.length > 0;
          const isOpen: boolean = hasChildren && group.id === openGroupId;

          return (
            <li key={group.id}>
              <Link
                to={{ hash: `#${group.id}` }}
                aria-current={group.id === activeId ? "true" : undefined}
                aria-expanded={hasChildren ? isOpen : undefined}
                className={`flex items-start gap-1 break-words border-l-2 py-1 pl-3 pr-1 transition-colors ${
                  group.id === activeId
                    ? "border-primary font-semibold text-primary"
                    : "border-gray-200 text-gray-500 hover:text-gray-700"
                }`}
              >
                {hasChildren && (
                  <ChevronRightIcon
                    aria-hidden="true"
                    className={`mt-0.5 h-4 w-4 shrink-0 transition-transform ${
                      isOpen ? "rotate-90" : ""
                    }`}
                  />
                )}
                <span className="min-w-0">{t(group.translationKey)}</span>
              </Link>
              {isOpen && (
                <ul>
                  {group.children.map((child) => (
                    <li key={child.id}>
                      <Link
                        to={{ hash: `#${child.id}` }}
                        aria-current={
                          child.id === activeId ? "true" : undefined
                        }
                        className={`block break-words border-l-2 py-1 pl-6 pr-1 transition-colors ${
                          child.id === activeId
                            ? "border-primary font-semibold text-primary"
                            : "border-gray-200 text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        {t(child.translationKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
